import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Ban, CalendarClock, Play, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { platformApi } from '../api'
import { TenantStatusBadge } from '../components/TenantStatusBadge'

/**
 * One customer: what they are on, what they are using, and what has happened to them.
 *
 * <p>Every action asks for a reason before it will run. The reason is not decoration — it is
 * written to the subscription history and to the operator audit, and a workspace that went
 * dark for an unrecorded reason is one nobody can safely bring back.
 */
export function PlatformTenantDetailPage() {
  const { id = '' } = useParams()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [planCode, setPlanCode] = useState('')
  const [seats, setSeats] = useState('')
  const [trialDays, setTrialDays] = useState('14')
  const [error, setError] = useState<string | null>(null)

  const tenant = useQuery({
    queryKey: ['platform', 'tenant', id],
    queryFn: () => platformApi.tenant(id),
    enabled: Boolean(id),
  })
  const plans = useQuery({ queryKey: ['platform', 'plans'], queryFn: () => platformApi.plans() })

  function afterAction() {
    setReason('')
    setError(null)
    void queryClient.invalidateQueries({ queryKey: ['platform'] })
  }

  const act = useMutation({
    mutationFn: async (action: 'suspend' | 'resume' | 'cancel' | 'plan' | 'trial') => {
      switch (action) {
        case 'suspend':
          return platformApi.suspend(id, reason)
        case 'resume':
          return platformApi.resume(id)
        case 'cancel':
          return platformApi.cancel(id, reason, 30)
        case 'plan':
          return platformApi.changePlan(id, planCode, seats ? Number(seats) : null, reason)
        case 'trial':
          return platformApi.extendTrial(id, Number(trialDays), reason)
      }
    },
    onSuccess: afterAction,
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'That did not work.'
      setError(message)
    },
  })

  if (tenant.isPending) {
    return <p className="text-[var(--color-muted)]">Loading…</p>
  }
  if (!tenant.data) {
    return <p className="text-[var(--color-danger)]">That workspace could not be read.</p>
  }

  const detail = tenant.data
  const summary = detail.summary
  const needsReason = reason.trim().length === 0

  return (
    <div className="space-y-6">
      <Link
        to="/platform/tenants"
        className="inline-flex items-center gap-1 text-sm text-[var(--color-muted)] hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden />
        All workspaces
      </Link>

      <div className="flex flex-wrap items-start gap-4">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-heading)]">{summary.name}</h1>
          <p className="text-sm text-[var(--color-muted)]">
            {summary.slug} · created {new Date(summary.createdAt).toLocaleDateString()}
          </p>
        </div>
        <div className="ms-auto">
          <TenantStatusBadge status={summary.status} />
        </div>
      </div>

      {detail.suspendedReason && (
        <div className="rounded-[var(--radius-control)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          Suspended: {detail.suspendedReason}
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-[var(--radius-control)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="space-y-4 lg:col-span-2">
          <Panel title="Plan and usage">
            <dl className="grid gap-4 sm:grid-cols-3">
              <Field label="Plan" value={summary.planCode ?? '—'} />
              <Field
                label="Employees"
                value={`${summary.activeEmployees ?? '—'}${detail.maxEmployees ? ` / ${detail.maxEmployees}` : ''}`}
              />
              <Field
                label="Users"
                value={`${summary.activeUsers ?? '—'}${detail.maxUsers ? ` / ${detail.maxUsers}` : ''}`}
              />
              <Field
                label="Storage"
                value={
                  summary.storageBytes == null
                    ? '—'
                    : `${(summary.storageBytes / 1024 / 1024).toFixed(1)} MB${detail.maxStorageMb ? ` / ${detail.maxStorageMb} MB` : ''}`
                }
              />
              <Field label="Metered on" value={summary.usageAsOf ?? 'not yet metered'} />
              <Field label="Trial ends" value={summary.trialEndsOn ?? '—'} />
            </dl>
          </Panel>

          <Panel title="Contact">
            <dl className="grid gap-4 sm:grid-cols-3">
              <Field label="Billing email" value={detail.billingEmail ?? '—'} />
              <Field label="Contact" value={detail.contactName ?? '—'} />
              <Field label="Phone" value={detail.contactPhone ?? '—'} />
            </dl>
          </Panel>

          <Panel title="History">
            {detail.history.length === 0 ? (
              <p className="text-sm text-[var(--color-muted)]">Nothing recorded yet.</p>
            ) : (
              <ol className="space-y-3">
                {detail.history.map((event, index) => (
                  <li key={`${event.occurredAt}-${index}`} className="flex gap-3 text-sm">
                    <span className="text-[var(--color-muted)] tabular-nums">
                      {new Date(event.occurredAt).toLocaleDateString()}
                    </span>
                    <span className="font-medium text-[var(--color-heading)]">
                      {event.event.replace(/_/g, ' ').toLowerCase()}
                    </span>
                    {event.toPlan && (
                      <span className="text-[var(--color-muted)]">
                        {event.fromPlan ? `${event.fromPlan} → ${event.toPlan}` : event.toPlan}
                      </span>
                    )}
                    <span className="ms-auto text-[var(--color-muted)]">{event.actor}</span>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </section>

        <aside className="space-y-4">
          <Panel title="Actions">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="action-reason">Reason</Label>
                <Input
                  id="action-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Recorded against this workspace"
                />
                <p className="text-xs text-[var(--color-muted)]">
                  Written to the subscription history and the operator audit.
                </p>
              </div>

              {summary.status === 'SUSPENDED' ? (
                <Button
                  className="w-full"
                  onClick={() => act.mutate('resume')}
                  disabled={act.isPending}
                >
                  <Play className="me-2 size-4" aria-hidden />
                  Resume
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => act.mutate('suspend')}
                  disabled={needsReason || act.isPending}
                >
                  <Ban className="me-2 size-4" aria-hidden />
                  Suspend
                </Button>
              )}

              {summary.status === 'TRIAL' && (
                <div className="space-y-1.5">
                  <Label htmlFor="trial-days">Extend trial by (days)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="trial-days"
                      type="number"
                      min={1}
                      value={trialDays}
                      onChange={(event) => setTrialDays(event.target.value)}
                    />
                    <Button
                      variant="outline"
                      onClick={() => act.mutate('trial')}
                      disabled={act.isPending}
                    >
                      <CalendarClock className="size-4" aria-hidden />
                      <span className="sr-only">Extend trial</span>
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-1.5 border-t border-[var(--color-border)] pt-4">
                <Label htmlFor="plan-code">Move to plan</Label>
                <select
                  id="plan-code"
                  value={planCode}
                  onChange={(event) => setPlanCode(event.target.value)}
                  className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] px-3 text-sm"
                >
                  <option value="">Choose a plan…</option>
                  {(plans.data ?? []).map((plan) => (
                    <option key={plan.code} value={plan.code}>
                      {plan.name}
                    </option>
                  ))}
                </select>
                <Label htmlFor="plan-seats" className="pt-2">
                  Seats (optional)
                </Label>
                <Input
                  id="plan-seats"
                  type="number"
                  min={1}
                  value={seats}
                  onChange={(event) => setSeats(event.target.value)}
                  placeholder="Plan default"
                />
                <Button
                  className="w-full"
                  onClick={() => act.mutate('plan')}
                  disabled={!planCode || act.isPending}
                >
                  Change plan
                </Button>
              </div>

              <div className="border-t border-[var(--color-border)] pt-4">
                <Button
                  variant="danger"
                  className="w-full"
                  onClick={() => act.mutate('cancel')}
                  disabled={needsReason || act.isPending}
                >
                  <Trash2 className="me-2 size-4" aria-hidden />
                  Cancel workspace
                </Button>
                <p className="mt-2 text-xs text-[var(--color-muted)]">
                  Closes the workspace and schedules its data for deletion after 30 days.
                </p>
              </div>
            </div>
          </Panel>
        </aside>
      </div>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-5">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-[var(--color-muted)]">
        {title}
      </h2>
      {children}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-[var(--color-muted)]">{label}</dt>
      <dd className="text-sm font-medium text-[var(--color-heading)]">{value}</dd>
    </div>
  )
}
